import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  listLocations,
  createLocation,
  updateLocation,
  deleteLocation,
} from "../../api/locations";

const LOCATION_TYPES = ["hostel", "residential_school", "day_school", "other"];

const blank = () => ({
  name: "",
  locationType: "",
  address: "",
  notes: "",
  isActive: true,
});

export default function LocationsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    listLocations({ activeOnly: false })
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => setError(e.message || "Failed to load locations"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setEditing(blank()); setEditingId(null); };
  const openEdit = (row) => {
    setEditing({
      name: row.name || "",
      locationType: row.locationType || "",
      address: row.address || "",
      notes: row.notes || "",
      isActive: row.isActive !== false,
    });
    setEditingId(row.id);
  };
  const close = () => { setEditing(null); setEditingId(null); };

  const save = async () => {
    if (!editing.name.trim()) { setError("Name is required"); return; }
    const body = {
      name: editing.name.trim(),
      locationType: editing.locationType.trim() || null,
      address: editing.address.trim() || null,
      notes: editing.notes.trim() || null,
      isActive: !!editing.isActive,
    };
    try {
      setSaving(true);
      if (editingId) await updateLocation(editingId, body);
      else await createLocation(body);
      close();
      fetchAll();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete location "${row.name}"? If quotes, price rows or templates use it, it's made inactive instead.`)) return;
    try {
      setError("");
      // The API says whether it was deleted or, being in use, made inactive.
      const result = await deleteLocation(row.id);
      setNotice(result?.message || "");
      fetchAll();
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Locations</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchAll}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>New Location</Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">No locations</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.locationType || ""}</TableCell>
                <TableCell>{row.isActive ? "Yes" : "No"}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => remove(row)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editing !== null} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit Location" : "New Location"}</DialogTitle>
        <DialogContent>
          {editing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name" required fullWidth
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <TextField
                label="Type" fullWidth
                value={editing.locationType}
                onChange={(e) => setEditing({ ...editing, locationType: e.target.value })}
                helperText={`Suggested: ${LOCATION_TYPES.join(", ")}`}
                inputProps={{ list: "qb-location-types" }}
              />
              <datalist id="qb-location-types">
                {LOCATION_TYPES.map((t) => <option key={t} value={t} />)}
              </datalist>
              <TextField
                label="Address" fullWidth multiline minRows={2}
                value={editing.address}
                onChange={(e) => setEditing({ ...editing, address: e.target.value })}
              />
              <TextField
                label="Notes" fullWidth multiline minRows={2}
                value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!editing.isActive}
                    onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
